import {
  parseReaderReportType,
  REPORT_TYPE_VALUES,
  type ReaderReportType,
} from "@/lib/reports/report-type";
import { SUBSCRIPTION_COOKIE_VALUE } from "./subscription-constants";

const ORDERED_TYPES = [...REPORT_TYPE_VALUES];

export type SubscriptionPreferences = {
  editions: Set<ReaderReportType>;
};

const parseRawValue = (rawValue?: string | null): Set<ReaderReportType> => {
  if (!rawValue || rawValue.trim().length === 0) {
    return new Set();
  }

  if (rawValue === SUBSCRIPTION_COOKIE_VALUE) {
    return new Set(REPORT_TYPE_VALUES);
  }

  const tokens = rawValue
    .split(/[|,]/)
    .map((token) => token.trim().toLowerCase())
    .filter((token) => token.length > 0);

  const editions = new Set<ReaderReportType>();
  tokens.forEach((token) => {
    const parsed = parseReaderReportType(token);
    if (parsed) {
      editions.add(parsed);
    }
  });

  return editions;
};

export const createSubscriptionPreferences = (
  rawValue?: string | null
): SubscriptionPreferences => ({
  editions: parseRawValue(rawValue),
});

export const hasSubscriptionForEdition = (
  preferences: SubscriptionPreferences,
  reportType?: ReaderReportType
) => {
  if (!reportType) {
    return preferences.editions.size > 0;
  }

  return preferences.editions.has(reportType);
};

export const addEditionToPreferences = (
  preferences: SubscriptionPreferences,
  reportType: ReaderReportType
): SubscriptionPreferences => {
  const next = new Set(preferences.editions);
  next.add(reportType);
  return { editions: next };
};

export const serializeSubscriptionPreferences = (
  preferences: SubscriptionPreferences
) => {
  if (preferences.editions.size === 0) {
    return "";
  }

  if (preferences.editions.size === REPORT_TYPE_VALUES.length) {
    return SUBSCRIPTION_COOKIE_VALUE;
  }

  const ordered = Array.from(preferences.editions).sort(
    (a, b) => ORDERED_TYPES.indexOf(a) - ORDERED_TYPES.indexOf(b)
  );

  return ordered.join(",");
};

export const buildExclusiveSubscriptionCookieValue = (
  reportType: ReaderReportType
): string => {
  const preferences = {
    editions: new Set<ReaderReportType>([reportType]),
  } satisfies SubscriptionPreferences;

  return serializeSubscriptionPreferences(preferences);
};

export const mergeSubscriptionCookieValue = ({
  existingValue,
  nextEdition,
}: {
  existingValue?: string | null;
  nextEdition?: ReaderReportType;
}) => {
  const preferences = createSubscriptionPreferences(existingValue);
  const nextPreferences =
    nextEdition !== undefined
      ? addEditionToPreferences(preferences, nextEdition)
      : preferences;
  return serializeSubscriptionPreferences(nextPreferences);
};
